import { Core, Decorative } from "@faire/design-tokens";
import { trackFaireDirectPdpSignupBannerCtaClick } from "@faire/web-api/events/faireDirect/click/pdpSignupBannerCta";
import { Button } from "@faire/web/slate/Button";
import { Column } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useObservableWindow } from "@faire/web/ui/hooks/useObservableWindow";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import styled, { css } from "styled-components";

import { useFaireDirectBrand } from "@faire/retailer/hooks/FaireDirect/useFaireDirectBrand/useFaireDirectBrand";
import { useFaireDirectFirstOrderIncentive } from "@faire/retailer/hooks/FaireDirect/useFaireDirectFirstOrderIncentive/useFaireDirectFirstOrderIncentive";
import { useFaireDirectSignUp } from "@faire/retailer/hooks/FaireDirect/useFaireDirectSignUp/useFaireDirectSignUp";

export const AnimatedFaireDirectSignUpBanner: React.FC = () => {
  const [bannerHeight, setBannerHeight] = useState(0);
  const { isFaireDirectSignUpEligible } = useFaireDirectSignUp();
  const faireDirectSignUpBannerRef = useRef<HTMLDivElement | null>(null);
  const { width } = useObservableWindow();

  const contentNodeCallback = useCallback(
    (node: HTMLDivElement | null) => {
      if (node) {
        setBannerHeight(node.offsetHeight);
      }
      faireDirectSignUpBannerRef.current = node;
    },
    [setBannerHeight]
  );

  useEffect(() => {
    if (faireDirectSignUpBannerRef.current) {
      setBannerHeight(faireDirectSignUpBannerRef.current.offsetHeight);
    }
  }, [width, setBannerHeight]);

  if (!isFaireDirectSignUpEligible) {
    return null;
  }
  return (
    <>
      <TransitionWrapper $bannerHeight={bannerHeight}>
        <FaireDirectSignUpBanner ref={contentNodeCallback} />
      </TransitionWrapper>
      <Spacer height={bannerHeight} />
    </>
  );
};

const FaireDirectSignUpBanner = forwardRef<HTMLDivElement>((_, ref) => {
  const { faireDirectBrand } = useFaireDirectBrand();
  const brandName = faireDirectBrand?.name ?? "";
  const {
    isFaireDirectCreditPercentageBased,
    formattedPercentageBasedNumber,
    formattedFixedBasedAmount,
  } = useFaireDirectFirstOrderIncentive();

  const { faireDirectSignupRequiredEmailAddressSignUpRoute } =
    useFaireDirectSignUp();
  return (
    <BannerContainer align="center" ref={ref}>
      <Typography variant="displaySSerifRegular">
        <StrictLocalMsg
          defaultMessage="Sign up to shop {brandCompanyName}"
          description={{
            text: "Header on a banner encouraging users to sign up to shop the brand on Faire.",
            img: "https://cdn.faire.com/fastly/2030715aa1c798d52243c5fb6d2b98f2e3851ce93ecc52a6906ada494fa35e03.png",
          }}
          values={{
            brandCompanyName: brandName,
          }}
        />
      </Typography>
      <Spacer height="2x" />
      <Typography variant="subheadingSansRegular">
        {isFaireDirectCreditPercentageBased ? (
          <StrictLocalMsg
            defaultMessage="Get {percentageDiscountNumberWithUnit} off your first order and 1 year of free shipping."
            description={{
              text: "The benefits of shopping on Faire for a certain brand. Part of a banner encouraging users to sign up.",
              img: "https://cdn.faire.com/fastly/2030715aa1c798d52243c5fb6d2b98f2e3851ce93ecc52a6906ada494fa35e03.png",
            }}
            values={{
              percentageDiscountNumberWithUnit: formattedPercentageBasedNumber,
            }}
          />
        ) : (
          <StrictLocalMsg
            defaultMessage="Get {discountAmountMoney} off your first order and 1 year of free shipping."
            description={{
              text: "The benefits of shopping on Faire for a certain brand. Part of a banner encouraging users to sign up.",
              img: "https://cdn.faire.com/fastly/af7c0ac7d2ff2237c6d37bb0cd3cee7ab03feba5ad7120fe416170395c27c6ba.png",
            }}
            values={{
              discountAmountMoney: formattedFixedBasedAmount,
            }}
          />
        )}
      </Typography>
      <Spacer height="3x" />
      <SignUpButton
        linkTo={faireDirectSignupRequiredEmailAddressSignUpRoute}
        data-test-id="fd-sign-up-animated-banner-cta"
        onClick={trackFaireDirectPdpSignupBannerCtaClick}
      >
        <StrictLocalMsg
          defaultMessage="Sign up for free"
          description={{
            text: "Call to action to begin the sign up flow (CTA button text)",
            img: "https://cdn.faire.com/fastly/2030715aa1c798d52243c5fb6d2b98f2e3851ce93ecc52a6906ada494fa35e03.png",
          }}
        />
      </SignUpButton>
    </BannerContainer>
  );
});

const TransitionWrapper = styled.div<{ $bannerHeight: number }>`
  width: 100%;
  position: fixed;

  ${({ $bannerHeight }) =>
    $bannerHeight === 0
      ? css`
          z-index: -1;
        `
      : css`
          z-index: 2;
          animation: slide-up 300ms ease-out forwards;

          @keyframes slide-up {
            from {
              opacity: 0;
              bottom: ${-1 * $bannerHeight}px;
            }

            to {
              opacity: 100%;
              bottom: 0px;
            }
          }
        `}
`;

const BannerContainer = styled(Column)`
  border: 1px ${Core.border.muted};
  background-color: ${Decorative.tint.neutral};
  padding: ${getSpacing("6x")};

  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing("3x")};
  }
`;

const SignUpButton = styled(Button)`
  min-width: 216px;

  ${responsiveMediaQueries.mobile} {
    width: 100%;
  }
`;
